import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { inr } from "../lib/format";
import { SectionTitle, Pill, EmptyState } from "../components/common";
import { FeaturedCard } from "../components/monetization";
import { usePricing } from "../pricing";
import { Input } from "../components/ui/input";
import { Search, Building2, Plus, MapPin, Sparkles, SlidersHorizontal } from "lucide-react";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400";

export default function Properties() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role === "owner";
  const { config: pconf } = usePricing();
  const [listings, setListings] = useState([]);
  const [mine, setMine] = useState([]);
  const [filters, setFilters] = useState({ city: "", max_rent: "", bhk: "", furnishing: "" });
  const [showFilters, setShowFilters] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const { data } = await api.get("/listings", { params });
    setListings(data);
  }, [filters]);

  useEffect(() => { load(); if (isOwner) api.get("/listings/mine").then((r) => setMine(r.data)); }, [isOwner, load]);

  if (isOwner) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <SectionTitle>{t("my_properties")}</SectionTitle>
          <button onClick={() => navigate("/properties/new")} data-testid="add-property-wizard-btn" className="flex items-center gap-1 text-sm font-semibold text-terracotta bg-terracotta-light px-3 py-1.5 rounded-full"><Plus className="w-4 h-4" /> {t("add_property")}</button>
        </div>
        {mine.length === 0 ? <EmptyState icon={Building2} title={t("my_properties")} sub={t("add_property")} /> : (
          <div className="space-y-3">{mine.map((l) => (
            <div key={l.id} className="space-y-2">
              <ListingCard l={l} onClick={() => navigate(`/properties/${l.id}`)} />
              <FeaturedCard listingId={l.id} api={api} config={pconf} />
            </div>
          ))}</div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SectionTitle sub={t("best_matches")}>{t("find_home")}</SectionTitle>
      <div className="flex gap-2">
        <div className="flex-1 flex items-center gap-2 gs-card px-3">
          <Search className="w-4 h-4 text-slate-400" />
          <input className="flex-1 gs-tap bg-transparent outline-none text-sm" placeholder={t("city")} data-testid="search-city-input"
            value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        <button onClick={() => setShowFilters((s) => !s)} data-testid="toggle-filters-btn" className="gs-card gs-tap px-3 flex items-center text-slate-600"><SlidersHorizontal className="w-5 h-5" /></button>
      </div>
      {showFilters && (
        <div className="gs-card p-4 grid grid-cols-2 gap-3">
          <Input className="rounded-xl" placeholder={t("budget_max")} data-testid="filter-max-rent" value={filters.max_rent} onChange={(e) => setFilters({ ...filters, max_rent: e.target.value })} />
          <select className="rounded-xl border border-slate-200 gs-tap px-3 text-sm" data-testid="filter-bhk" value={filters.bhk} onChange={(e) => setFilters({ ...filters, bhk: e.target.value })}>
            <option value="">{t("bhk")}</option>{["1RK","1BHK","2BHK","3BHK","4BHK"].map((b) => <option key={b}>{b}</option>)}
          </select>
          <select className="rounded-xl border border-slate-200 gs-tap px-3 text-sm" data-testid="filter-furnishing" value={filters.furnishing} onChange={(e) => setFilters({ ...filters, furnishing: e.target.value })}>
            <option value="">{t("furnishing")}</option>{["Unfurnished","Semi-furnished","Fully furnished"].map((f) => <option key={f}>{f}</option>)}
          </select>
          <button onClick={() => { load(); setShowFilters(false); }} data-testid="apply-filters-btn" className="rounded-xl bg-terracotta text-white text-sm font-semibold">{t("filters")}</button>
        </div>
      )}
      <div className="flex items-center gap-1.5 text-xs font-semibold text-terracotta"><Sparkles className="w-3.5 h-3.5" /> {t("best_matches")}</div>
      {listings.length === 0 ? <EmptyState icon={Building2} title={t("find_home")} /> : (
        <div className="space-y-3">{listings.map((l) => <ListingCard key={l.id} l={l} onClick={() => navigate(`/properties/${l.id}`)} />)}</div>
      )}
    </div>
  );
}

function ListingCard({ l, onClick }) {
  const { t } = useLang();
  return (
    <button onClick={onClick} data-testid={`listing-card-${l.id}`} className="gs-card overflow-hidden w-full text-left active:scale-[0.99] transition">
      <img src={l.photos?.[0] || FALLBACK_IMG} alt="" className="w-full h-40 object-cover" onError={(e) => (e.target.src = FALLBACK_IMG)} />
      <div className="p-3.5">
        <div className="flex items-start justify-between gap-2">
          <div className="font-head font-bold text-slate-900 leading-tight">{l.title}</div>
          <Pill tone="slate">{l.bhk}</Pill>
        </div>
        <div className="flex items-center gap-1 text-xs text-slate-400 mt-1"><MapPin className="w-3 h-3" /> {l.locality}, {l.city}</div>
        <div className="flex items-center justify-between mt-2.5">
          <div className="font-mono font-bold text-terracotta text-lg">{inr(l.rent)}<span className="text-slate-400 text-xs font-body">{t("per_month")}</span></div>
          <div className="text-xs text-slate-500">{t("deposit")}: <span className="font-mono font-semibold">{inr(l.deposit)}</span></div>
        </div>
      </div>
    </button>
  );
}
